import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { useAppDispatch } from 'src/context/store';
import { user_userNotificationsFunc } from 'src/context/notifications/notifications';
import { user_readUserNotificationFunc } from 'src/context/notifications/user_readUserNotification';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { EmptyContent } from 'src/components/empty-content';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { NotificationItem } from './notification-item';

import type { INotificationItem } from './notification-item';

// ----------------------------------------------------------------------

export type NotificationsDrawerProps = IconButtonProps & {
  data?: INotificationItem[];
};

export function NotificationsDrawer({ data = [], sx, ...other }: NotificationsDrawerProps) {
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const dispatch = useAppDispatch();
  const [notificationData, setNotificationsData] = useState([] as INotificationItem[]);
  const user_userNotifications = useSelector((state: any) => state.user_userNotifications);

  useEffect(() => {
    dispatch(user_userNotificationsFunc());
  }, [dispatch]);

  useEffect(() => {
    setNotificationsData(user_userNotifications?.user_userNotificationsData);
  }, [user_userNotifications]);

  const totalUnRead =
    notificationData &&
    notificationData.length > 0 &&
    notificationData?.filter((item) => item.isRead === false).length;

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        minHeight: 68,
        display: 'flex',
        alignItems: 'center',
        outline: '2px solid',
        outlineColor: 'common.white',
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Bildirimler
      </Typography>

      <IconButton onClick={onClose}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Box>
  );

  const handleReadNotification = (notification: INotificationItem) => () => {
    if (notification.isRead) return;

    dispatch(
      user_readUserNotificationFunc({
        uid: notification.uid,
      })
    );
    dispatch(user_userNotificationsFunc());
  };

  const renderList = () => (
    <Scrollbar>
      {notificationData && notificationData.length === 0 && (
        <EmptyContent
          title="Bildirim Bulunamadı"
          description=""
          sx={{
            mt: 10,
            justifyContent: 'flex-start',
          }}
        />
      )}

      <Box component="ul">
        {notificationData &&
          notificationData.length > 0 &&
          notificationData?.map((notification: INotificationItem) => (
            <Box
              component="li"
              key={notification._id}
              sx={{ display: 'flex' }}
              onClick={handleReadNotification(notification)}
            >
              <NotificationItem notification={notification} />
            </Box>
          ))}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Notifications button"
        onClick={onOpen}
        sx={{
          ...sx,
          border: '2px solid',
          borderColor: 'common.white',
        }}
        {...other}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <SvgIcon>
            <path
              fill="#fff"
              d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
              opacity="0.5"
            />
            <path
              fill="#fff"
              d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead()}
        {renderList()}
      </Drawer>
    </>
  );
}
